import type {
  CatalogAccessRequest,
  Configuration,
  CreateConnectorInput,
  CreateDashboardInput,
  CreateDataCatalogInput,
  CreateGraphInput,
  CreateIngestionPipelineInput,
  CreateJobTemplateInput,
  CreateNotebookInput,
  CreatePrivateLinkInput,
  UpdatePrivateLinkInput,
  CreateSavedQueryInput,
  CreateSparkEngineInput,
  CreateSqlEngineInput,
  CreateVpcPeeringInput,
  CreateZeroEtlPolicyInput,
  UpdateWorkspaceBucketPolicyInput,
  UpdateWorkspaceLoggingSettingsInput,
  UpdateWorkspaceMetricsSettingsInput,
  ExecuteNotebookCodeInput,
  StartNotebookSessionInput,
  StartSparkJobInput,
  StartSqlQueryExecutionInput,
  UpdateDashboardInput,
  UpdateGraphInput,
  UpdateIngestionPipelineInput,
  UpdateJobTemplateInput,
  UpdateSavedQueryInput,
  UpdateTableMaintenanceConfigInput,
  UpdateZeroEtlPolicyInput,
} from '@repo/api-gw-sdk';
import { LakehouseApi } from '@repo/api-gw-sdk';

import type { HttpClient } from './httpClient';

const baseUrl = '/lakehouse';

/**
 * Wraps an async function to handle 204 No Content responses gracefully.
 * The generated SDK tries to parse empty 204 responses as JSON, throwing a SyntaxError.
 * This wrapper catches that specific error and treats it as success.
 */
const handle204 = async <T>(fn: () => Promise<T>): Promise<T | undefined> => {
  try {
    return await fn();
  } catch (error) {
    // 204 No Content causes JSON parsing to fail - treat as success
    if (
      error instanceof SyntaxError ||
      (error instanceof Error &&
        error.message.includes('Unexpected end of JSON input'))
    ) {
      return undefined;
    }
    throw error;
  }
};

export const lakehouse = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const lakehouseApi = new LakehouseApi(config);

  return {
    // Workspace operations (project-level, no workspaceId needed)
    listWorkspaces: () => lakehouseApi.listWorkspaces(currentProjectId),

    // SQL Engine operations
    useListSqlEngines: (workspaceId: string | null) =>
      httpClient.execute(
        lakehouseApi.listSqlEngines.bind(lakehouseApi),
        baseUrl,
        'listSqlEngines',
        currentProjectId,
        workspaceId ?? ''
      ),
    createSqlEngine: (workspaceId: string, request: CreateSqlEngineInput) =>
      lakehouseApi.createSqlEngine(currentProjectId, workspaceId, request),
    deleteSqlEngine: (workspaceId: string, sqlEngineId: string) =>
      handle204(() =>
        lakehouseApi.deleteSqlEngine(currentProjectId, workspaceId, sqlEngineId)
      ),
    startSqlEngine: (workspaceId: string, sqlEngineId: string) =>
      lakehouseApi.startSqlEngine(currentProjectId, workspaceId, sqlEngineId),
    stopSqlEngine: (workspaceId: string, sqlEngineId: string) =>
      lakehouseApi.stopSqlEngine(currentProjectId, workspaceId, sqlEngineId),

    // SQL Query Execution operations
    useListSqlQueryExecutions: (workspaceId: string | null) =>
      httpClient.execute(
        lakehouseApi.listSqlQueryExecutions.bind(lakehouseApi),
        baseUrl,
        'listSqlQueryExecutions',
        currentProjectId,
        workspaceId ?? ''
      ),
    startSqlQueryExecution: (
      workspaceId: string,
      request: StartSqlQueryExecutionInput
    ) =>
      lakehouseApi.startSqlQueryExecution(
        currentProjectId,
        workspaceId,
        request
      ),
    getSqlQueryExecution: (workspaceId: string, sqlQueryExecutionId: string) =>
      lakehouseApi.getSqlQueryExecution(
        currentProjectId,
        workspaceId,
        sqlQueryExecutionId
      ),
    getSqlQueryExecutionResult: (
      workspaceId: string,
      sqlQueryExecutionId: string,
      pageToken?: string,
      pageSize?: number
    ) =>
      lakehouseApi.getSqlQueryExecutionResult(
        currentProjectId,
        workspaceId,
        sqlQueryExecutionId,
        pageToken,
        pageSize
      ),

    // Spark Engine operations
    useListSparkEngines: (workspaceId: string | null) =>
      httpClient.execute(
        lakehouseApi.listSparkEngines.bind(lakehouseApi),
        baseUrl,
        'listSparkEngines',
        currentProjectId,
        workspaceId ?? ''
      ),
    createSparkEngine: (workspaceId: string, request: CreateSparkEngineInput) =>
      lakehouseApi.createSparkEngine(currentProjectId, workspaceId, request),
    getSparkEngine: (workspaceId: string, sparkEngineId: string) =>
      lakehouseApi.getSparkEngine(currentProjectId, workspaceId, sparkEngineId),
    deleteSparkEngine: (workspaceId: string, sparkEngineId: string) =>
      handle204(() =>
        lakehouseApi.deleteSparkEngine(
          currentProjectId,
          workspaceId,
          sparkEngineId
        )
      ),
    startSparkEngine: (workspaceId: string, sparkEngineId: string) =>
      lakehouseApi.startSparkEngine(
        currentProjectId,
        workspaceId,
        sparkEngineId
      ),
    stopSparkEngine: (workspaceId: string, sparkEngineId: string) =>
      lakehouseApi.stopSparkEngine(
        currentProjectId,
        workspaceId,
        sparkEngineId
      ),

    // Spark Job operations
    useListSparkJobs: (workspaceId: string | null) =>
      httpClient.execute(
        lakehouseApi.listSparkJobs.bind(lakehouseApi),
        baseUrl,
        'listSparkJobs',
        currentProjectId,
        workspaceId ?? ''
      ),
    startSparkJob: (workspaceId: string, request: StartSparkJobInput) =>
      lakehouseApi.startSparkJob(currentProjectId, workspaceId, request),

    // Job Template operations
    useListJobTemplates: (workspaceId: string | null) =>
      httpClient.execute(
        lakehouseApi.listJobTemplates.bind(lakehouseApi),
        baseUrl,
        'listJobTemplates',
        currentProjectId,
        workspaceId ?? ''
      ),
    createJobTemplate: (workspaceId: string, request: CreateJobTemplateInput) =>
      lakehouseApi.createJobTemplate(currentProjectId, workspaceId, request),
    updateJobTemplate: (
      workspaceId: string,
      jobTemplateId: string,
      request: UpdateJobTemplateInput
    ) =>
      lakehouseApi.updateJobTemplate(
        currentProjectId,
        workspaceId,
        jobTemplateId,
        request
      ),
    deleteJobTemplate: (workspaceId: string, jobTemplateId: string) =>
      handle204(() =>
        lakehouseApi.deleteJobTemplate(
          currentProjectId,
          workspaceId,
          jobTemplateId
        )
      ),

    // Saved Query operations
    useListSavedQueries: (workspaceId: string | null) =>
      httpClient.execute(
        lakehouseApi.listSavedQueries.bind(lakehouseApi),
        baseUrl,
        'listSavedQueries',
        currentProjectId,
        workspaceId ?? ''
      ),
    createSavedQuery: (workspaceId: string, request: CreateSavedQueryInput) =>
      lakehouseApi.createSavedQuery(currentProjectId, workspaceId, request),
    getSavedQuery: (workspaceId: string, savedQueryId: string) =>
      lakehouseApi.getSavedQuery(currentProjectId, workspaceId, savedQueryId),
    updateSavedQuery: (
      workspaceId: string,
      savedQueryId: string,
      request: UpdateSavedQueryInput
    ) =>
      lakehouseApi.updateSavedQuery(
        currentProjectId,
        workspaceId,
        savedQueryId,
        request
      ),
    deleteSavedQuery: (workspaceId: string, savedQueryId: string) =>
      handle204(() =>
        lakehouseApi.deleteSavedQuery(
          currentProjectId,
          workspaceId,
          savedQueryId
        )
      ),

    // Ingestion Pipeline operations
    useListIngestionPipelines: (workspaceId: string | null) =>
      httpClient.execute(
        lakehouseApi.listIngestionPipelines.bind(lakehouseApi),
        baseUrl,
        'listIngestionPipelines',
        currentProjectId,
        workspaceId ?? ''
      ),
    createIngestionPipeline: (
      workspaceId: string,
      request: CreateIngestionPipelineInput
    ) =>
      lakehouseApi.createIngestionPipeline(
        currentProjectId,
        workspaceId,
        request
      ),
    deleteIngestionPipeline: (
      workspaceId: string,
      ingestionPipelineId: string
    ) =>
      handle204(() =>
        lakehouseApi.deleteIngestionPipeline(
          currentProjectId,
          workspaceId,
          ingestionPipelineId
        )
      ),
    startIngestionPipeline: (
      workspaceId: string,
      ingestionPipelineId: string
    ) =>
      lakehouseApi.startIngestionPipeline(
        currentProjectId,
        workspaceId,
        ingestionPipelineId
      ),
    stopIngestionPipeline: (workspaceId: string, ingestionPipelineId: string) =>
      lakehouseApi.stopIngestionPipeline(
        currentProjectId,
        workspaceId,
        ingestionPipelineId
      ),
    updateIngestionPipeline: (
      workspaceId: string,
      ingestionPipelineId: string,
      request: UpdateIngestionPipelineInput
    ) =>
      lakehouseApi.updateIngestionPipeline(
        currentProjectId,
        workspaceId,
        ingestionPipelineId,
        request
      ),

    // Connector operations
    useListConnectors: (workspaceId: string | null) =>
      httpClient.execute(
        lakehouseApi.listConnectors.bind(lakehouseApi),
        baseUrl,
        'listConnectors',
        currentProjectId,
        workspaceId ?? ''
      ),
    listConnectors: (workspaceId: string) =>
      lakehouseApi.listConnectors(currentProjectId, workspaceId),
    createConnector: (workspaceId: string, request: CreateConnectorInput) =>
      lakehouseApi.createConnector(currentProjectId, workspaceId, request),
    deleteConnector: (workspaceId: string, connectorId: string) =>
      handle204(() =>
        lakehouseApi.deleteConnector(currentProjectId, workspaceId, connectorId)
      ),
    activateConnector: (workspaceId: string, connectorId: string) =>
      lakehouseApi.activateConnector(
        currentProjectId,
        workspaceId,
        connectorId
      ),
    deactivateConnector: (workspaceId: string, connectorId: string) =>
      lakehouseApi.deactivateConnector(
        currentProjectId,
        workspaceId,
        connectorId
      ),

    // Data Catalog operations
    useListDataCatalogs: (workspaceId: string | null) =>
      httpClient.execute(
        lakehouseApi.listDataCatalogs.bind(lakehouseApi),
        baseUrl,
        'listDataCatalogs',
        currentProjectId,
        workspaceId ?? ''
      ),
    listDataCatalogs: (workspaceId: string) =>
      lakehouseApi.listDataCatalogs(currentProjectId, workspaceId),
    createDataCatalog: (workspaceId: string, request: CreateDataCatalogInput) =>
      lakehouseApi.createDataCatalog(currentProjectId, workspaceId, request),
    deleteDataCatalog: (workspaceId: string, catalogId: string) =>
      handle204(() =>
        lakehouseApi.deleteDataCatalog(currentProjectId, workspaceId, catalogId)
      ),
    accessDataCatalog: (
      workspaceId: string,
      catalogId: string,
      request: CatalogAccessRequest
    ) =>
      lakehouseApi.accessDataCatalog(
        currentProjectId,
        workspaceId,
        catalogId,
        request
      ),

    // Notebook operations
    useListNotebooks: (workspaceId: string | null) =>
      httpClient.execute(
        lakehouseApi.listNotebooks.bind(lakehouseApi),
        baseUrl,
        'listNotebooks',
        currentProjectId,
        workspaceId ?? ''
      ),
    createNotebook: (workspaceId: string, request: CreateNotebookInput) =>
      lakehouseApi.createNotebook(currentProjectId, workspaceId, request),
    getNotebook: (workspaceId: string, notebookId: string) =>
      lakehouseApi.getNotebook(currentProjectId, workspaceId, notebookId),
    deleteNotebook: (workspaceId: string, notebookId: string) =>
      handle204(() =>
        lakehouseApi.deleteNotebook(currentProjectId, workspaceId, notebookId)
      ),
    getNotebookContent: (workspaceId: string, notebookId: string) =>
      lakehouseApi.getNotebookContent(
        currentProjectId,
        workspaceId,
        notebookId
      ),
    saveNotebookContent: (
      workspaceId: string,
      notebookId: string,
      content: object
    ) =>
      handle204(() =>
        lakehouseApi.saveNotebookContent(
          currentProjectId,
          workspaceId,
          notebookId,
          {
            content,
          }
        )
      ),

    // Notebook Session operations
    startNotebookSession: (
      workspaceId: string,
      notebookId: string,
      request: StartNotebookSessionInput
    ) =>
      lakehouseApi.startNotebookSession(
        currentProjectId,
        workspaceId,
        notebookId,
        request
      ),
    getNotebookSession: (workspaceId: string, notebookId: string) =>
      lakehouseApi.getNotebookSession(
        currentProjectId,
        workspaceId,
        notebookId
      ),
    stopNotebookSession: (workspaceId: string, notebookId: string) =>
      lakehouseApi.stopNotebookSession(
        currentProjectId,
        workspaceId,
        notebookId
      ),

    // Notebook Execution operations
    executeNotebookCode: (
      workspaceId: string,
      notebookId: string,
      request: ExecuteNotebookCodeInput
    ) =>
      lakehouseApi.executeNotebookCode(
        currentProjectId,
        workspaceId,
        notebookId,
        request
      ),
    getNotebookExecutionResult: (
      workspaceId: string,
      notebookId: string,
      executionId: string
    ) =>
      lakehouseApi.getNotebookExecutionResult(
        currentProjectId,
        workspaceId,
        notebookId,
        executionId
      ),

    // Dashboard operations
    useListDashboards: (workspaceId: string | null) =>
      httpClient.execute(
        lakehouseApi.listDashboards.bind(lakehouseApi),
        baseUrl,
        'listDashboards',
        currentProjectId,
        workspaceId ?? ''
      ),
    getDashboard: (workspaceId: string, dashboardId: string) =>
      lakehouseApi.getDashboard(currentProjectId, workspaceId, dashboardId),
    createDashboard: (workspaceId: string, input: CreateDashboardInput) =>
      lakehouseApi.createDashboard(currentProjectId, workspaceId, input),
    updateDashboard: (
      workspaceId: string,
      dashboardId: string,
      input: UpdateDashboardInput
    ) =>
      lakehouseApi.updateDashboard(
        currentProjectId,
        workspaceId,
        dashboardId,
        input
      ),
    deleteDashboard: (workspaceId: string, dashboardId: string) =>
      handle204(() =>
        lakehouseApi.deleteDashboard(currentProjectId, workspaceId, dashboardId)
      ),

    // Dashboard Graph operations
    createGraph: (
      workspaceId: string,
      dashboardId: string,
      input: CreateGraphInput
    ) =>
      lakehouseApi.createGraph(
        currentProjectId,
        workspaceId,
        dashboardId,
        input
      ),
    getGraph: (workspaceId: string, dashboardId: string, graphId: string) =>
      lakehouseApi.getGraph(
        currentProjectId,
        workspaceId,
        dashboardId,
        graphId
      ),
    updateGraph: (
      workspaceId: string,
      dashboardId: string,
      graphId: string,
      input: UpdateGraphInput
    ) =>
      lakehouseApi.updateGraph(
        currentProjectId,
        workspaceId,
        dashboardId,
        graphId,
        input
      ),
    deleteGraph: (workspaceId: string, dashboardId: string, graphId: string) =>
      handle204(() =>
        lakehouseApi.deleteGraph(
          currentProjectId,
          workspaceId,
          dashboardId,
          graphId
        )
      ),

    getBucketPolicy: (workspaceId: string) =>
      lakehouseApi.getWorkspaceBucketPolicy(currentProjectId, workspaceId),
    updateBucketPolicy: (
      workspaceId: string,
      input: UpdateWorkspaceBucketPolicyInput
    ) =>
      lakehouseApi.updateWorkspaceBucketPolicy(
        currentProjectId,
        workspaceId,
        input
      ),
    getNetworkingSettings: (workspaceId: string) =>
      lakehouseApi.getWorkspaceNetworkingSettings(
        currentProjectId,
        workspaceId
      ),
    createVpcPeering: (workspaceId: string, input: CreateVpcPeeringInput) =>
      lakehouseApi.createWorkspaceVpcPeering(
        currentProjectId,
        workspaceId,
        input
      ),
    deleteVpcPeering: (workspaceId: string) =>
      handle204(() =>
        lakehouseApi.deleteWorkspaceVpcPeering(currentProjectId, workspaceId)
      ),
    createPrivateLink: (workspaceId: string, input: CreatePrivateLinkInput) =>
      lakehouseApi.createWorkspacePrivateLink(
        currentProjectId,
        workspaceId,
        input
      ),
    updatePrivateLink: (
      workspaceId: string,
      privateLinkId: string,
      input: UpdatePrivateLinkInput
    ) =>
      lakehouseApi.updateWorkspacePrivateLink(
        currentProjectId,
        workspaceId,
        privateLinkId,
        input
      ),
    deletePrivateLink: (workspaceId: string, privateLinkId: string) =>
      handle204(() =>
        lakehouseApi.deleteWorkspacePrivateLink(
          currentProjectId,
          workspaceId,
          privateLinkId
        )
      ),

    // Observability settings
    getLoggingSettings: (workspaceId: string) =>
      lakehouseApi.getWorkspaceLoggingSettings(currentProjectId, workspaceId),
    updateLoggingSettings: (
      workspaceId: string,
      input: UpdateWorkspaceLoggingSettingsInput
    ) =>
      lakehouseApi.updateWorkspaceLoggingSettings(
        currentProjectId,
        workspaceId,
        input
      ),
    getMetricsSettings: (workspaceId: string) =>
      lakehouseApi.getWorkspaceMetricsSettings(currentProjectId, workspaceId),
    updateMetricsSettings: (
      workspaceId: string,
      input: UpdateWorkspaceMetricsSettingsInput
    ) =>
      lakehouseApi.updateWorkspaceMetricsSettings(
        currentProjectId,
        workspaceId,
        input
      ),

    // Table Maintenance operations
    getTableMaintenanceConfig: (
      workspaceId: string,
      catalogId: string,
      databaseName: string,
      tableName: string
    ) =>
      lakehouseApi.getTableMaintenanceConfig(
        currentProjectId,
        workspaceId,
        catalogId,
        databaseName,
        tableName
      ),
    updateTableMaintenanceConfig: (
      workspaceId: string,
      catalogId: string,
      databaseName: string,
      tableName: string,
      input: UpdateTableMaintenanceConfigInput
    ) =>
      lakehouseApi.updateTableMaintenanceConfig(
        currentProjectId,
        workspaceId,
        catalogId,
        databaseName,
        tableName,
        input
      ),

    // Zero-ETL Policy operations
    useListZeroEtlPolicies: (workspaceId: string | null) =>
      httpClient.execute(
        lakehouseApi.listZeroEtlPolicies.bind(lakehouseApi),
        baseUrl,
        'listZeroEtlPolicies',
        currentProjectId,
        workspaceId ?? ''
      ),
    createZeroEtlPolicy: (
      workspaceId: string,
      request: CreateZeroEtlPolicyInput
    ) =>
      lakehouseApi.createZeroEtlPolicy(currentProjectId, workspaceId, request),
    updateZeroEtlPolicy: (
      workspaceId: string,
      zeroEtlPolicyId: string,
      request: UpdateZeroEtlPolicyInput
    ) =>
      lakehouseApi.updateZeroEtlPolicy(
        currentProjectId,
        workspaceId,
        zeroEtlPolicyId,
        request
      ),
    deleteZeroEtlPolicy: (workspaceId: string, zeroEtlPolicyId: string) =>
      handle204(() =>
        lakehouseApi.deleteZeroEtlPolicy(
          currentProjectId,
          workspaceId,
          zeroEtlPolicyId
        )
      ),
  };
};
